import i18n from "i18next";
import { useFlags } from "launchdarkly-react-client-sdk";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import {
  CLAIMS_GENERIC_FILTER,
  PARTS_GENERIC_FILTER,
  SIGNAL_EVENTS_GENERIC_FILTER,
  SMART_GROUPS_FILTER,
} from "shared/filterDefinitions";

import { FilterOperator, FilterType } from "features/ui/Filters/types";
import { SchemaEntry } from "features/ui/MaterialTable";

import { PHOTOS_ACCESSOR, VEHICLE_ACCESSOR } from "./constants";
import useDealersSchema from "./dealerSchema";
import useGeneralDescriptionSchema from "./generalDescriptionSchema";
import { SchemaEntryOverride, SchemaRelations, UseSchema } from "./types";
import { getGroupBySelectOptions } from "./utils/group-by-utils";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
  sortAndFilterSchemaByWhitelistedAccessors,
} from "./utils/schema-utils";
import useVehiclesSchema from "./vehiclesSchema";

const VALUES_ENDPOINT = "claims";

export const useClaimsSchema = (
  // be default we want to hide the vehicle accessor, but we still want to allow it to be shown if needed.
  accessorsToHide: string[] = [VEHICLE_ACCESSOR, PHOTOS_ACCESSOR],
  orderedWhitelistedAccessors?: string[]
): UseSchema => {
  const t = i18n.t;
  const { warrantyClaimsCost: warrantyClaimsCostFF } = useFlags();
  const { attributes } = useAttributesContext(ContextProviderName.Claim);
  const vehiclesSchema = useVehiclesSchema();

  const dealerSchema = useDealersSchema();
  const generalDescriptionSchema = useGeneralDescriptionSchema();

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const getFilter = getFilterFunction(
    CLAIMS_GENERIC_FILTER,
    attributeMap,
    VALUES_ENDPOINT
  );

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const overrides: SchemaEntryOverride = {
    // Do not allow filtering on "Vehicle" via UI. We use a separate query param for that.
    [VEHICLE_ACCESSOR]: {
      schemaOverride: {
        filter: undefined,
      },
    },
    similarClaimID: {
      schemaOverride: {
        filter: CLAIMS_GENERIC_FILTER({
          fieldName: "similarClaimID",
          fieldNameForAPI: "ID",
          label: t("pages:claim_analytics.similar_claim_id"),
          whitelistedFilterOperators: [FilterOperator.IN],
        }),
      },
    },
    costTotal: {
      ignore: !warrantyClaimsCostFF,
    },
    costLabor: {
      ignore: !warrantyClaimsCostFF,
    },
    costParts: {
      ignore: !warrantyClaimsCostFF,
    },
    costOther: {
      ignore: !warrantyClaimsCostFF,
    },
    totalApprovedNetCost: {
      ignore: !warrantyClaimsCostFF,
    },
    relatedSignalEventOccurrences: {
      filterOverride: SIGNAL_EVENTS_GENERIC_FILTER({
        fieldName: "relatedSignalEventOccurrences",
        fieldNameForAPI: "ID",
        label: getDisplayLabel(
          "relatedSignalEventOccurrences",
          t("common:columns.related_signal_events")
        ),
        search: true,
        disableFiltering: true,
        disableContainsFilters: true,
        filterType: FilterType.OCCURS,
        baseEntityText: t("common:event_type.claim_other"),
      }),
    },
    relatedClaims: {
      filterOverride: CLAIMS_GENERIC_FILTER({
        fieldName: "relatedClaims",
        fieldNameForAPI: "ID",
        label: getDisplayLabel(
          "relatedClaims",
          t("common:columns.related_claims")
        ),
        search: true,
        disableFiltering: true,
        disableContainsFilters: true,
        filterType: FilterType.OCCURS,
        baseEntityText: t("common:event_type.claim_other"),
      }),
    },
    mentionedSignalEvents: {
      schemaOverride: {
        filter: SIGNAL_EVENTS_GENERIC_FILTER({
          fieldName: "mentionedSignalEvents",
          fieldNameForAPI: "ID",
          label: getDisplayLabel(
            "mentionedSignalEvents",
            t("pages:claim_analytics.mentioned_signal_events")
          ),
          search: true,
          disableFiltering: true,
          disableContainsFilters: true,
        }),
      },
    },
    nonCausalParts: {
      schemaOverride: {
        filter: PARTS_GENERIC_FILTER({
          fieldName: "nonCausalParts",
          fieldNameForAPI: "ID",
          label: getDisplayLabel(
            "nonCausalParts",
            t("pages:claim_analytics.non_causal_parts")
          ),
          search: true,
          disableFiltering: true,
          disableContainsFilters: true,
        }),
      },
    },
    smartGroups: {
      schemaOverride: {
        filter: SMART_GROUPS_FILTER({
          fieldName: "smartGroups",
          fieldNameForAPI: "ID",
          label: getDisplayLabel(
            "smartGroups",
            t("common:columns.smart_groups")
          ),
          disableArbitraryText: true,
        }),
      },
    },
    vehiclePhotos: {
      filterOverride: {
        isCount: true,
      },
    },
    vehicleAttachments: {
      filterOverride: {
        isCount: true,
      },
    },
  };

  const relations: SchemaRelations = {
    "/api/v1/dealers": dealerSchema,
    "/api/v1/parts": generalDescriptionSchema,
    "/api/v1/laborCodes": generalDescriptionSchema,
    "/api/v1/vehicles": vehiclesSchema,
  };

  const groupByRelations: SchemaRelations = {
    "/api/v1/dealers": dealerSchema,
    "/api/v1/vehicles": vehiclesSchema,
  };

  const schema: SchemaEntry[] = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    relations,
    accessorsToHide
  );

  const attributesForGroupBy = attributes?.filter(
    ({ ID }) => !accessorsToHide.includes(ID)
  );

  const groupBySelectOptions = getGroupBySelectOptions(
    attributesForGroupBy,
    groupByRelations
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  return {
    schema: sortAndFilterSchemaByWhitelistedAccessors(
      schema,
      orderedWhitelistedAccessors
    ),
    attributes,
    groupBySelectOptions,
    getDisplayLabel,
    attributeMap,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaEntry,
    getSchemaForAccessor,
  };
};
